import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';

/**
 * Toast — bottom-right notification snackbar with auto-dismiss + queue.
 *
 * Derived from M3 Snackbar per Agent Protocol step 2:
 *   - Spec:      https://m3.material.io/components/snackbar/overview
 *   - Reference: Material Web has no first-party snackbar at time of
 *                authoring; M3 spec drives the visual.
 *
 * Populus adaptations applied:
 *   - Bottom-right placement (vs M3 default bottom-center; matches
 *     business-app convention where the right side is "operator-tool
 *     output" zone — same reasoning as the right-sticky ActivityPanel)
 *   - Auto-dismiss after `duration` ms (default 4000); pause-on-hover
 *     deferred (low Populus demand)
 *   - Queue of multiple toasts stacked vertically (newest at bottom);
 *     M3 spec only shows one at a time but business apps fire multiple
 *     status messages in close succession (mark paid → invoice
 *     auto-updated → recompute fired)
 *   - 4 semantic variants: success / error / info / warning — maps to
 *     the same Status palette as Badge / ActivityPanel (semantic
 *     consistency across the org tier)
 *   - Optional `action` (label + onClick) for "Undo" / "View" affordances
 *   - Context-based API (useToast()) — components push without prop
 *     drilling; ToastProvider wraps the app tree
 *
 * Token discipline:
 *   - Surface → --color-card (system; operator-tool chrome)
 *   - Status accent (left border) → --color-status-* (semantic; never
 *     per-org)
 *   - Action button (if used) → Button atom (ghost variant) — inherits
 *     atom's --brand-primary
 *
 * Phase 11 Block 6 PR 5 (2026-05-12).
 */

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: ReactNode;
  variant?: ToastVariant;
  duration?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
  toast: (t: Omit<Toast, 'id'>) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

const accentColor: Record<ToastVariant, string> = {
  success: 'var(--color-status-green)',
  error: 'var(--color-status-red)',
  info: 'var(--color-status-celadon)',
  warning: 'var(--color-status-amber)',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = useCallback((t: Omit<Toast, 'id'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts(prev => [...prev, { ...t, id }]);
  }, []);

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      {/* The live region is rendered empty from the start: a screen reader only
          announces content that is ADDED to a region it already knows about, so a
          region mounted together with its first toast is often read as nothing. */}
      <div
        aria-live="polite"
        aria-relevant="additions text"
        style={{
          position: 'fixed',
          bottom: 'var(--sp-5)',
          right: 'var(--sp-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-2)',
          zIndex: 1000,
          pointerEvents: 'none',
        }}
      >
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

interface ToastItemProps {
  toast: Toast;
  onDismiss: () => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  useEffect(() => {
    const duration = toast.duration ?? 4000;
    if (duration <= 0) return;
    const timer = window.setTimeout(onDismiss, duration);
    return () => window.clearTimeout(timer);
  }, [toast.id, toast.duration, onDismiss]);

  const variant = toast.variant ?? 'info';
  return (
    <div
      // An error interrupts (assertive); everything else waits its turn inside
      // the polite region above.
      role={variant === 'error' ? 'alert' : undefined}
      style={{
        background: 'var(--color-card)',
        color: 'var(--color-ink)',
        border: '1px solid var(--color-border)',
        borderLeft: `3px solid ${accentColor[variant]}`,
        borderRadius: 'var(--r-md)',
        boxShadow: 'var(--sh-lg)',
        padding: 'var(--sp-3) var(--sp-4)',
        minWidth: 280,
        maxWidth: 420,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3)',
        pointerEvents: 'auto',
        fontSize: 'var(--fs-md)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>{toast.message}</div>
      {toast.action && (
        <button
          type="button"
          onClick={() => {
            toast.action!.onClick();
            onDismiss();
          }}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--brand-primary)',
            fontWeight: 'var(--fw-medium)',
            fontSize: 'var(--fs-sm)',
            cursor: 'pointer',
            padding: 'var(--sp-1) var(--sp-2)',
            borderRadius: 'var(--r-sm)',
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--color-muted)',
          fontSize: 'var(--fs-lg)',
          cursor: 'pointer',
          padding: 0,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}
